/**
 * Multi-rol por email. Un mismo email puede tener varios documentos en `users`
 * (residente / admin / super admin / colaborador / comercial), cada uno con su
 * propio `userid` y `typeuser`. Este módulo resuelve qué rol activar al entrar
 * (recordando la última plataforma abierta) y expone la lista de roles para el
 * selector de plataforma del perfil.
 */
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase";

// Etiquetas de plataforma por typeuser.
export const ROLE_LABELS = {
  0: "Residentes",
  1: "Administración",
  2: "Super Admin",
  3: "Colaboradores",
  4: "Comercial",
};

// Pantalla de inicio por tipo de usuario.
export function homeForType(t) {
  const n = Number(t);
  return n === 2 ? "homesuperadmin"
    : n === 1 ? "homeadmin"
    : n === 3 ? "homeemployee"
    : n === 4 ? "homecomercial"
    : "inicio";
}

// Todos los roles (docs de `users`) que comparten un email. Dedup por typeuser.
export async function getRolesForEmail(email) {
  const mail = String(email || "").trim();
  if (!mail) return [];
  const snap = await getDocs(query(collection(db, "users"), where("email", "==", mail)));
  const roles = snap.docs.map((d) => {
    const data = d.data();
    const nombre = data?.name
      || `${data?.NombreCompleto || ""} ${data?.ApellidosCompleto || ""}`.trim();
    return {
      docId: d.id,
      userid: data?.userid != null && data?.userid !== "" ? Number(data.userid) : "",
      typeuser: Number(data?.typeuser ?? 0),
      name: nombre,
      email: mail,
    };
  });
  const seen = new Set();
  return roles.filter((r) => (seen.has(r.typeuser) ? false : (seen.add(r.typeuser), true)));
}

// El auto-arreglo de activación al loguearse (antes reconcileActivationOnLogin,
// que escribía users desde el navegador) es ahora el callable vincularMiCuenta
// (functions/identity/cuenta.js), llamado desde App.js.

// ── Última plataforma abierta (persistida por email en localStorage) ──
const LS_KEY = (email) => `habitia_active_role_${String(email || "").toLowerCase()}`;

export function getLastRoleDocId(email) {
  try { return localStorage.getItem(LS_KEY(email)) || null; } catch (_) { return null; }
}
export function setLastRoleDocId(email, docId) {
  try { localStorage.setItem(LS_KEY(email), docId); } catch (_) { /* modo privado / sin storage */ }
}

// Prioridad por defecto cuando no hay elección previa guardada.
const PRIORITY = { 2: 0, 1: 1, 4: 2, 3: 3, 0: 4 };

export function resolveActiveRole(email, roles) {
  if (!roles || !roles.length) return null;
  const lastId = getLastRoleDocId(email);
  const found = lastId && roles.find((r) => r.docId === lastId);
  if (found) return found;
  return [...roles].sort((a, b) => (PRIORITY[a.typeuser] ?? 9) - (PRIORITY[b.typeuser] ?? 9))[0];
}
